/**
 * Browser-side sealing of a venue API credential, for the DIME credential
 * store.
 *
 * The user types an exchange API key into this page. It is encrypted HERE and
 * leaves the browser already opaque: the backend that receives it holds no
 * permission to decrypt it, and neither does anything else except the proxy pod
 * belonging to this user's own agent. That is the entire point of the feature,
 * so this module is the one place a credential exists in plaintext, and it never
 * writes one anywhere — not to storage, not to a log, not to an error message.
 *
 * ── The format ──────────────────────────────────────────────────────────────
 *
 * An envelope, not a direct RSA encryption, because RSA-4096 OAEP caps near 446
 * bytes of payload — enough for an API key but not for the PEM private keys some
 * venues issue, and one format for both beats two:
 *
 *   a fresh random AES-256-GCM key   encrypts the credential
 *   the server's RSA public key      wraps that AES key
 *
 * The AES key is generated here and never leaves; unwrapping it requires
 * `kms:Decrypt` on a key held in AWS KMS.
 *
 * The AES layer authenticates `dime-credential/v1/<namespace>/<label>` as
 * additional data. That binding is what stops a sealed credential being copied
 * into another account's slot and opened there: one KMS key serves every user
 * and asymmetric decryption accepts no per-user scoping condition, so the
 * binding lives in this layer instead. Get it wrong and the credential stores
 * fine and fails months later at the moment the agent needs it — which is why
 * `namespace` must be the value the server returned alongside the public key,
 * never one this page assembles.
 *
 * ── Staying in step with the other two implementations ──────────────────────
 *
 * The same format is implemented in Go twice over: `api/terminal/pkg/secrets`
 * (which defines it) and the credential proxy (which opens it). All three must
 * agree byte for byte. `tests/credential-envelope.test.ts` pins the same fixed
 * vectors as the Go tests — if one of those fails, do not adjust the constant:
 * one implementation has drifted and the other two can no longer open what it
 * produces.
 */

/** Envelope format version. Checked exactly by the reader, not as a minimum. */
export const CREDENTIAL_ENVELOPE_VERSION = 1;

/**
 * Names both layers. `RSA_OAEP_SHA_256` is WebCrypto's
 * `{name:'RSA-OAEP', hash:'SHA-256'}` and AWS KMS's `RSAES_OAEP_SHA_256` — the
 * same primitive under two names, with an EMPTY OAEP label on both sides (KMS
 * accepts no label, so this must not pass one).
 */
export const CREDENTIAL_ENVELOPE_ALG = 'RSA_OAEP_SHA_256+AES_256_GCM';

/** AES-256. */
const KEY_BYTES = 32;
/**
 * GCM's standard 96-bit nonce — the only size WebCrypto and Go's
 * `cipher.NewGCM` interoperate on without special handling.
 */
const NONCE_BYTES = 12;

/**
 * The sealed credential, exactly as it crosses the wire and is stored. Byte
 * fields are standard base64 WITH padding (what `btoa` produces and what Go's
 * `base64.StdEncoding` reads) — not the URL-safe or unpadded variants, so there
 * is one encoding for all three implementations to agree on.
 */
export interface CredentialEnvelope {
  readonly v: number;
  readonly alg: string;
  /**
   * The key this was sealed to, echoed from the server's response. Diagnostics
   * and key rotation only — the reader never selects a key from it.
   */
  readonly kid: string;
  readonly wrapped_key: string;
  readonly nonce: string;
  readonly ciphertext: string;
}

/**
 * The additional data a credential envelope is bound to.
 *
 * Both arguments are canonicalized so that this side and the two Go sides
 * cannot disagree over case or stray whitespace. The API restricts labels to
 * `[a-z0-9-]` anyway, making the canonicalization a backstop rather than the
 * thing being relied on.
 *
 * Deliberately NOT carried inside the envelope: it is context each end derives,
 * and additional data supplied alongside the ciphertext protects nothing.
 */
export function credentialAad(namespace: string, label: string): string {
  const canon = (s: string): string => s.trim().toLowerCase();
  return `dime-credential/v1/${canon(namespace)}/${canon(label)}`;
}

/** What `sealCredential` needs. All of it comes from the server's key response. */
export interface SealCredentialInput {
  /** SPKI public key, PEM-armoured, from `GET /v1/credentials/public-key`. */
  readonly publicKeyPem: string;
  /** The key's identifier, echoed into the envelope. */
  readonly keyId: string;
  /** The caller's credential namespace, from the same response. */
  readonly namespace: string;
  /** The label this credential is being stored under. */
  readonly label: string;
  /** The credential itself. Never persisted, logged, or included in an error. */
  readonly secret: string;
}

/**
 * Seals a credential into an envelope the backend can store and only the user's
 * own proxy can open.
 */
export async function sealCredential(
  input: SealCredentialInput,
): Promise<CredentialEnvelope> {
  const subtle = requireSubtleCrypto();
  if (input.secret.length === 0) {
    throw new Error('Cannot store an empty credential');
  }

  const publicKey = await importSpkiPublicKey(subtle, input.publicKeyPem);

  // Extractable because the wrapped copy is the whole point — it has to be
  // exported to be sent. It exists only inside this call.
  const dataKey = await subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt'],
  );
  const nonce = randomBytes(NONCE_BYTES);
  const aad = new TextEncoder().encode(
    credentialAad(input.namespace, input.label),
  );

  const ciphertext = await subtle.encrypt(
    { name: 'AES-GCM', iv: nonce, additionalData: aad },
    dataKey,
    new TextEncoder().encode(input.secret),
  );

  const rawDataKey = new Uint8Array(await subtle.exportKey('raw', dataKey));
  if (rawDataKey.byteLength !== KEY_BYTES) {
    throw new Error('Generated data key is the wrong size');
  }
  try {
    // No `label` — see CREDENTIAL_ENVELOPE_ALG. KMS accepts none, so passing
    // one here would produce a blob nothing can unwrap.
    const wrappedKey = await subtle.encrypt(
      { name: 'RSA-OAEP' },
      publicKey,
      rawDataKey,
    );
    return {
      v: CREDENTIAL_ENVELOPE_VERSION,
      alg: CREDENTIAL_ENVELOPE_ALG,
      kid: input.keyId,
      wrapped_key: toBase64(new Uint8Array(wrappedKey)),
      nonce: toBase64(nonce),
      ciphertext: toBase64(new Uint8Array(ciphertext)),
    };
  } finally {
    // The credential's own key, in a buffer we own. Overwriting it is not a
    // guarantee — the engine may have copied it, and the plaintext string is
    // beyond reach entirely — but it removes the copy we can remove.
    rawDataKey.fill(0);
  }
}

/**
 * Parses a PEM SPKI public key for RSA-OAEP-SHA-256.
 *
 * Strict about the armour: a key that arrives malformed must fail here, where
 * the message can say so, rather than inside `encrypt` as an opaque
 * `OperationError`.
 */
async function importSpkiPublicKey(
  subtle: SubtleCrypto,
  pem: string,
): Promise<CryptoKey> {
  const body = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s+/g, '');
  if (body.length === 0 || body === pem.replace(/\s+/g, '')) {
    throw new Error('Credential public key is not an SPKI PEM block');
  }
  let der: Uint8Array<ArrayBuffer>;
  try {
    der = fromBase64(body);
  } catch {
    throw new Error('Credential public key is not valid base64');
  }
  try {
    return await subtle.importKey(
      'spki',
      der,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt'],
    );
  } catch {
    throw new Error('Credential public key could not be imported');
  }
}

/**
 * WebCrypto, or a clear failure.
 *
 * `crypto.subtle` is undefined on an insecure origin, so a page served over
 * plain HTTP cannot seal anything. Saying that plainly beats a
 * `Cannot read properties of undefined` from the middle of the flow — and the
 * alternative, a JavaScript crypto fallback, would put the credential through
 * code we cannot audit as carefully.
 */
function requireSubtleCrypto(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (subtle == null) {
    throw new Error(
      'Storing a credential needs WebCrypto, which this browser only exposes on a secure (https) origin',
    );
  }
  return subtle;
}

/**
 * Typed as `Uint8Array<ArrayBuffer>`, not bare `Uint8Array`: WebCrypto's
 * `BufferSource` excludes views over a `SharedArrayBuffer`, and TypeScript 5.7+
 * makes bare `Uint8Array` mean "over either kind". Allocating the buffer
 * explicitly is what pins it to the non-shared one these calls accept.
 */
function randomBytes(n: number): Uint8Array<ArrayBuffer> {
  const buf = new Uint8Array(new ArrayBuffer(n));
  globalThis.crypto.getRandomValues(buf);
  return buf;
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function fromBase64(b64: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(s.length));
  for (let i = 0; i < s.length; i += 1) out[i] = s.charCodeAt(i);
  return out;
}
